import React from 'react';
import { useNavigate } from 'react-router-dom';
import PageContainer from '../../components/layout/PageContainer/PageContainer';

const TOOL_NAMES = [
  'Apollo', 'Hunter.io', 'LinkedIn Sales Nav', 'WebLeads',
  'ZoomInfo', 'Snov.io', 'Leadiro', 'RocketReach', 'Seamless.ai', 'Outscraper',
];

export const ComparisonToolLanding: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-light">
      {/* ── Hero ── */}
      <div
        className="relative text-center overflow-hidden"
        style={{
          background: 'linear-gradient(160deg, #2D3142 0%, #3d4560 100%)',
          padding: '80px 40px 96px',
        }}
      >
        {/* Dot grid */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            opacity: 0.07,
            backgroundImage: 'radial-gradient(circle, #fff 1px, transparent 1px)',
            backgroundSize: '28px 28px',
          }}
        />
        {/* Crimson blob */}
        <div
          className="absolute pointer-events-none"
          style={{
            top: -80, right: -80,
            width: 400, height: 400,
            borderRadius: '50%',
            background: '#8A3033',
            opacity: 0.12,
            filter: 'blur(80px)',
          }}
        />

        <div className="relative z-10 max-w-2xl mx-auto">
          <div
            className="inline-flex items-center gap-2 mb-8"
            style={{
              background: 'rgba(255,255,255,0.1)',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: 99,
              padding: '6px 16px',
            }}
          >
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-400" />
            <span className="text-sm font-medium" style={{ color: 'rgba(255,255,255,0.85)' }}>
              18+ tools compared &amp; verified
            </span>
          </div>

          <h1
            className="font-bold text-white leading-tight mb-5"
            style={{ fontSize: 'clamp(36px, 5vw, 64px)', letterSpacing: '-1.5px' }}
          >
            Find Your Best<br />
            <span style={{ color: '#f87171' }}>Lead Generation</span> Tool
          </h1>
          <p className="text-lg mb-10" style={{ color: 'rgba(255,255,255,0.65)', lineHeight: 1.6 }}>
            Answer 5 quick questions. Get a personalized ranking of the tools that actually fit
            your industry, market, and workflow.
          </p>

          <button
            onClick={() => navigate('/comparison-tool/form')}
            className="inline-flex items-center gap-3 bg-primary text-white font-bold rounded-xl transition-all hover:-translate-y-0.5"
            style={{
              fontSize: 17,
              padding: '16px 36px',
              boxShadow: '0 8px 32px rgba(138,48,51,0.5)',
            }}
          >
            Start Comparing <span aria-hidden>→</span>
          </button>
          <p className="mt-4 text-sm" style={{ color: 'rgba(255,255,255,0.4)' }}>
            Takes less than 2 minutes · No account needed
          </p>
        </div>
      </div>

      {/* ── Tool ticker ── */}
      <div
        className="bg-white border-b border-gray-100 px-10 py-4 flex items-center gap-3 overflow-x-auto"
        style={{ scrollbarWidth: 'none' }}
      >
        <span className="text-xs font-semibold text-gray-400 uppercase tracking-widest whitespace-nowrap">
          We compare
        </span>
        <div className="flex gap-2 flex-wrap">
          {TOOL_NAMES.map((t) => (
            <span
              key={t}
              className="px-3 py-1 bg-light border border-gray-200 rounded-full text-sm font-medium text-secondary whitespace-nowrap"
            >
              {t}
            </span>
          ))}
          <span className="px-3 py-1 text-sm text-gray-400 whitespace-nowrap">+ 8 more</span>
        </div>
      </div>

      {/* ── Feature cards ── */}
      <div className="max-w-5xl mx-auto px-10 py-16 grid md:grid-cols-3 gap-6">
        {[
          {
            icon: '⚡',
            title: 'Under 2 minutes',
            body: '5 focused questions. No fluff. Get your ranked shortlist immediately.',
          },
          {
            icon: '📊',
            title: 'Data-driven scores',
            body: 'Pricing, accuracy, features, and location coverage, all weighted for your situation.',
          },
          {
            icon: '✅',
            title: 'Hands-on verified',
            body: "Every tool has been tested by our team. No affiliate bias in the scoring.",
          },
        ].map((card) => (
          <div
            key={card.title}
            className="bg-white rounded-2xl border border-gray-100 p-7 transition-all hover:-translate-y-1 hover:shadow-elevated cursor-default"
          >
            <div className="w-11 h-11 rounded-xl flex items-center justify-center text-xl mb-4"
              style={{ background: 'rgba(138,48,51,0.08)' }}>
              {card.icon}
            </div>
            <h3 className="font-bold text-base text-secondary mb-2">{card.title}</h3>
            <p className="text-sm text-gray-500 leading-relaxed">{card.body}</p>
          </div>
        ))}
      </div>

      {/* ── Bottom CTA strip ── */}
      <div className="max-w-5xl mx-auto px-10 pb-20">
        <div
          className="flex items-center justify-between gap-8 flex-wrap rounded-2xl p-10"
          style={{
            background: '#FDF0F0',
            border: '1px solid rgba(138,48,51,0.15)',
          }}
        >
          <div>
            <h2 className="text-2xl font-bold text-primary mb-2" style={{ letterSpacing: '-0.5px' }}>
              Ready to find your match?
            </h2>
            <p className="text-sm text-gray-500">
              Join thousands of sales teams who've used this to pick the right tool.
            </p>
          </div>
          <button
            onClick={() => navigate('/comparison-tool/form')}
            className="bg-primary text-white font-bold rounded-xl hover:bg-primary-hover transition-all whitespace-nowrap shadow-button hover:shadow-elevated hover:-translate-y-0.5"
            style={{ padding: '14px 32px', fontSize: 15 }}
          >
            Get My Recommendations →
          </button>
        </div>
      </div>
    </div>
  );
};

export default ComparisonToolLanding;
